import { Injectable } from '@angular/core';
import createAnalytics, {
  ZipAnalyticsInstance,
  withDefaults,
  DEFAULT_CROSS_DOMAIN_CSS_CLASS,
} from '@zip/analytics-core';
import { v4 } from 'uuid';
import { environment } from 'src/environments/environment';
import {
  ExperimentEnrolmentEvent,
  ExperimentEnrolmentSchema,
  FunnelContext,
  FunnelIdentifiers,
  FunnelOutcomeSchema,
  FunnelSource,
  FunnelStartSchema,
  Section,
  SectionOutcomeSchema,
  SectionStartSchema,
  UIClickEventData,
  UIClickSchema,
  UIInputEventData,
  UIInputSchema,
} from './events';

export { DEFAULT_CROSS_DOMAIN_CSS_CLASS };

// Concatenated so the CI `sed` that injects the real key can never rewrite this comparison.
const AMPLITUDE_KEY_PLACEHOLDER = '__AMPLITUDE_' + 'PROJECT_KEY__';

/**
 * A key is only usable once CI has injected it: not empty, not the un-replaced placeholder
 * (local builds), and not an unexpanded GitLab variable reference like `$ZIP_AMPLITUDE_...`.
 */
const isAmplitudeKeyInjected = (apiKey: string): boolean =>
  !!apiKey && apiKey !== AMPLITUDE_KEY_PLACEHOLDER && !apiKey.startsWith('$');

@Injectable({
  providedIn: 'root',
})
export class TrxService {
  analytics: ZipAnalyticsInstance;
  private static EnrolledInExperiments: Record<string, boolean> = {};
  private _funnelId: string;
  private currentSectionId: string;
  private currentSection: Section;

  constructor() {
    this.analytics = createAnalytics({
      ...withDefaults(
        environment.zipAnalytics.appId,
        environment.zipAnalytics.namespace,
        environment.zipAnalytics.endpoint,
      ),
      pageTracking: {
        enabled: true,
        linkClickTracking: {
          trackContent: true,
          enabled: true,
        },
      },
      fullStory: {
        enabled: true,
        samplingMethod: 'static' as never,
        staticSampleSize: 10,
        fsLibOptions: {
          orgId: 'ZEQ9T',
          host: 'fullstory.com',
          script: 'edge.fullstory.com/s/fs.js',
          namespace: 'FS',
        },
      },
      sessionTracking: true,
      sessionReplay: {
        enabled:
          environment.sessionReplay.enabled &&
          isAmplitudeKeyInjected(environment.sessionReplay.apiKey),
        apiKey: environment.sessionReplay.apiKey,
        sampleRate: environment.sessionReplay.sampleRate,
      },
    });
  }

  get domainUserId() {
    return this.analytics.state.tracker.getDomainUserId();
  }

  get userId() {
    return this.analytics.state.tracker.getUserId();
  }

  async setCustomerId(customerId: string) {
    await this.analytics.identify(customerId);
  }

  async sendPageEvent() {
    await this.analytics.page();
  }

  async sendExperimentEvent(experimentName: string, experimentValue: string) {
    if (TrxService.EnrolledInExperiments[experimentName]) return;

    const data: ExperimentEnrolmentEvent = {
      experiment_id: experimentName,
      variation_id: experimentValue,
    };

    await this.analytics.trackEvent({
      schema: ExperimentEnrolmentSchema.v1_0_0,
      data,
    });

    TrxService.EnrolledInExperiments[experimentName] = true;
  }

  async sendUIClickEvent(event: UIClickEventData) {
    await this.analytics.trackEvent({
      schema: UIClickSchema.v1_0_0,
      data: event,
    });
  }

  async sendUIInputEvent(event: UIInputEventData) {
    await this.analytics.trackEvent({
      schema: UIInputSchema.v1_0_0,
      data: event,
    });
  }

  private get funnelId() {
    return sessionStorage.getItem('loyalty-gift-cards-funnelId') ?? this._funnelId;
  }

  private set funnelId(id: string) {
    this._funnelId = id;
    sessionStorage.setItem('loyalty-gift-cards-funnelId', id);
  }

  resetFunnel() {
    sessionStorage.removeItem('loyalty-gift-cards-funnelId');
    this.funnelId = undefined;
  }

  async funnelStartEvent(source?: FunnelSource | null) {
    if (this.funnelId) return;
    this.funnelId = v4();
    await this.analytics.trackEvent(
      {
        schema: FunnelStartSchema.v1_0_4,
        data: {
          funnel_instance_id: this.funnelId,
          funnel_type: 'giftcards_web',
          funnel_source: source,
        },
      },
      this.funnelContext,
    );
  }

  async funnelSuccessEvent(outcomeReason?: string, attemptedWith?: string) {
    await this.analytics.trackEvent(
      {
        schema: FunnelOutcomeSchema.v1_0_1,
        data: {
          funnel_instance_id: this.funnelId,
          outcome: 'success',
          outcome_reason: outcomeReason,
          attempted_with: attemptedWith,
        },
      },
      this.funnelContext,
    );
    this.resetFunnel();
  }

  async funnelFailedEvent(outcomeReason?: string, attemptedWith?: string) {
    await this.analytics.trackEvent(
      {
        schema: FunnelOutcomeSchema.v1_0_1,
        data: {
          funnel_instance_id: this.funnelId,
          outcome: 'failure',
          outcome_reason: outcomeReason,
          attempted_with: attemptedWith,
        },
      },
      this.funnelContext,
    );
    this.resetFunnel();
  }

  async sectionStartEvent(name: Section) {
    this.currentSectionId = v4();
    this.currentSection = name;
    await this.analytics.trackEvent(
      {
        schema: SectionStartSchema.v1_0_1,
        data: {
          section_instance_id: this.currentSectionId,
          section_name: this.currentSection,
          is_server_side_section: false,
        },
      },
      this.funnelContext,
    );
  }

  async sectionSuccessEvent(outcomeReason?: string, attemptedWith?: string) {
    await this.analytics.trackEvent(
      {
        schema: SectionOutcomeSchema.v1_0_1,
        data: {
          section_instance_id: this.currentSectionId,
          section_name: this.currentSection,
          outcome: 'success',
          outcome_reason: outcomeReason,
          attempted_with: attemptedWith,
          is_server_side_section: false,
        },
      },
      this.funnelContext,
    );
  }

  async sectionFailedEvent(outcomeReason?: string, attemptedWith?: string) {
    await this.analytics.trackEvent(
      {
        schema: SectionOutcomeSchema.v1_0_1,
        data: {
          section_instance_id: this.currentSectionId,
          section_name: this.currentSection,
          outcome: 'failure',
          outcome_reason: outcomeReason,
          attempted_with: attemptedWith,
          is_server_side_section: false,
        },
      },
      this.funnelContext,
    );
  }

  private get funnelContext(): FunnelContext[] {
    return [
      {
        schema: FunnelIdentifiers.v1_0_2,
        data: {
          funnel_instance_id: this.funnelId,
          customer_id: this.analytics.state.tracker.getUserId() as unknown as string,
        },
      },
    ];
  }
}
